import { SelectData } from "../../../../models";
import {
  COMPLETED_COLUMN_ID,
  KANBAN_COLUMN_ORDER,
  OBJECT_TYPE_LABELS,
  TASK_TYPE_LABELS,
  UNASSIGNED_COLUMN_ID,
} from "../constants";
import { KanbanColumnId, KanbanTask, KanbanTaskType, TasksDashboardFilterKey, TasksDashboardFilters } from "../models";

/** A task nobody owns. Ordinarily this is also its status, since a task is given a status of
 *  Unassigned exactly while it has no assignee. */
export const isUnassigned = (task: KanbanTask): boolean => !task.assigneeName;

/** A task is either workflow-driven or general — exactly one of the two. */
export const getTaskType = (task: KanbanTask): KanbanTaskType => (task.hasWorkflow ? "workflow" : "general");

// Separator for the composite object-filter key. A NUL byte can't occur in a real object name, so
// joining type + name with it guarantees distinct (type, name) pairs never collide.
const OBJECT_KEY_SEPARATOR = String.fromCharCode(0);

/**
 * Stable identity for the "Project or deal" filter. Two linked objects can share a display name
 * (a project and a deal both called "Foo", or two projects of the same name), so the name alone
 * can't tell them apart — key on object type + name instead. Returns null for unlinked tasks.
 */
export const getObjectFilterKey = (task: KanbanTask): string | null =>
  task.linkedObjectName ? `${task.linkedObjectType ?? ""}${OBJECT_KEY_SEPARATOR}${task.linkedObjectName}` : null;

/**
 * The column a task is displayed in — its status, except that a task with no assignee falls to
 * Unassigned.
 *
 * Completed wins over that fallback. A finished task can end up with no assignee (a workflow applied
 * past its deadline auto-completes tasks nobody ever picked up, leaving `assignee` null against a
 * "Complete" status), and showing it as unassigned work reads as something still to do. It also broke
 * the hide toggles: such a task is only ever returned by the *completed* status query, so hiding
 * completed made a card disappear from the Unassigned column — the toggles looked coupled.
 */
export const resolveTaskColumn = (task: KanbanTask): KanbanColumnId => {
  if (task.status === COMPLETED_COLUMN_ID) return COMPLETED_COLUMN_ID;

  return isUnassigned(task) ? UNASSIGNED_COLUMN_ID : task.status;
};

/** Group tasks into their columns, preserving array order within each column. */
export const groupTasksByColumn = (tasks: KanbanTask[]): Record<KanbanColumnId, KanbanTask[]> => {
  const grouped = KANBAN_COLUMN_ORDER.reduce(
    (acc, columnId) => {
      acc[columnId] = [];
      return acc;
    },
    {} as Record<KanbanColumnId, KanbanTask[]>
  );

  tasks.forEach((task) => {
    grouped[resolveTaskColumn(task)].push(task);
  });

  return grouped;
};

/** Whether a multi-select filter's selection admits `value`. An unset filter admits everything. */
const matchesSelection = (selected: string[] | null | undefined, value: string | null | undefined): boolean =>
  !selected?.length || (value != null && selected.includes(value));

/**
 * Apply the filters to the task list: the values within one filter match as OR, and the filters
 * themselves are additive (AND). Empty/null filter selections are ignored.
 */
export const applyFilters = (tasks: KanbanTask[], filters: TasksDashboardFilters): KanbanTask[] =>
  tasks.filter((task) => {
    if (!matchesSelection(filters.assignee, task.assigneeName)) return false;
    if (!matchesSelection(filters.taskType, getTaskType(task))) return false;
    if (!matchesSelection(filters.objectName, getObjectFilterKey(task))) return false;
    // A task matches the tag filter on any one of its tags, so this is a set intersection.
    if (filters.tag?.length && !(task.tags ?? []).some((tag) => filters.tag?.includes(tag))) return false;
    return true;
  });

const toSelectData = (values: string[]): SelectData => values.map((value) => ({ key: value, value }));

/** Derive the available options for each filter dropdown from the current task set. */
export const buildFilterOptions = (tasks: KanbanTask[]): Record<TasksDashboardFilterKey, SelectData> => {
  const assignees = new Set<string>();
  const taskTypes = new Set<KanbanTaskType>();
  // Composite key (type + name) → display parts, so two objects that share a name but differ by
  // type (or are otherwise distinct) each keep their own option instead of overwriting each other.
  const objects = new Map<string, { name: string; type?: string }>();
  const tags = new Set<string>();

  tasks.forEach((task) => {
    if (task.assigneeName) assignees.add(task.assigneeName);
    taskTypes.add(getTaskType(task));
    const objectKey = getObjectFilterKey(task);
    if (objectKey) objects.set(objectKey, { name: task.linkedObjectName, type: task.linkedObjectType });
    (task.tags ?? []).forEach((tag) => tags.add(tag));
  });

  return {
    assignee: toSelectData(Array.from(assignees)),
    taskType: Array.from(taskTypes).map((type) => ({ key: type, value: TASK_TYPE_LABELS[type] })),
    objectName: Array.from(objects.entries()).map(([key, { name, type }]) => ({
      key,
      value: type ? `${name} · ${OBJECT_TYPE_LABELS[type] ?? type}` : name,
    })),
    tag: toSelectData(Array.from(tags)),
  };
};

/** Whether any filter currently has a selection applied. */
export const hasActiveFilters = (filters: TasksDashboardFilters): boolean =>
  Object.values(filters).some((values) => Boolean(values?.length));

/**
 * Move one or more tasks into a column, inserting them as a contiguous block (preserving their
 * relative order) immediately before `beforeId` — the rendered card at the drop point. When
 * `beforeId` is null (dropped past the last visible card in the column) the block is appended after
 * the column's last task. Anchoring on the rendered card keeps the insertion aligned with the drop
 * placeholder even when filters hide tasks, or a no-assignee task renders in the Unassigned lane
 * rather than its raw-status column.
 */
export const moveTasks = (
  tasks: KanbanTask[],
  taskIds: string[],
  toStatus: KanbanColumnId,
  beforeId: string | null
): KanbanTask[] => {
  const movingSet = new Set(taskIds);
  const moving = tasks.filter((task) => movingSet.has(task.id)).map((task) => ({ ...task, status: toStatus }));
  if (moving.length === 0) return tasks;

  const remaining = tasks.filter((task) => !movingSet.has(task.id));

  let insertAt: number;
  if (beforeId !== null) {
    const anchorPosition = remaining.findIndex((task) => task.id === beforeId);
    insertAt = anchorPosition === -1 ? remaining.length : anchorPosition;
  } else {
    // No anchor card: append after the last task that renders in this column, using the same
    // membership rule as the board's columns so other columns' ordering is left untouched.
    let lastInColumn = -1;
    remaining.forEach((task, index) => {
      if (resolveTaskColumn(task) === toStatus) lastInColumn = index;
    });
    insertAt = lastInColumn === -1 ? remaining.length : lastInColumn + 1;
  }

  return [...remaining.slice(0, insertAt), ...moving, ...remaining.slice(insertAt)];
};
